import PropTypes from 'prop-types';
import { BsEnvelope } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Card from '#globalComponents/Card/Card';
import FormField from '#globalComponents/Forms/FormField';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';

import EmailSequenceTimeline from '../EmailSequenceTimeline/EmailSequenceTimeline';

import CustomVariablesCard from './CustomVariablesCard';
import RecipientsCard from './RecipientsCard';

const OverviewTab = ({
	campaign,
	campaignId,
	campaignName,
	setCampaignName,
	emailAccountId,
	setEmailAccountId,
	isLoadingAccounts,
	connectedAccounts,
	transformedEmails,
	onDeleteEmail,
	selectedLists,
	selectedListIds,
	allContactLists,
	totalRecipients,
	isEditingLists,
	setIsEditingLists,
	onListToggle,
	onNavigate,
}) => (
	<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
		{/* Left Column - Settings & Email Sequence */}
		<div className="lg:col-span-2 space-y-6">
			{/* Settings Card */}
			<Card>
				<div className="flex flex-row items-center justify-between space-y-0 pb-2">
					<h3 className="text-md font-semibold tracking-tight">Einstellungen</h3>
				</div>
				<div className="pt-0 space-y-4">
					<FormField label="Kampagnen-Name" htmlFor="campaignName">
						<TextInput
							id="campaignName"
							placeholder="z.B. Produkt Launch 2024"
							value={campaignName}
							onChange={(e) => setCampaignName(e.target.value)}
						/>
					</FormField>

					<div className="space-y-2">
						<label className="text-sm font-medium leading-none">
							E-Mail-Konto *
							<span className="text-xs text-bgBase-800 ml-2 font-normal">
								(Erforderlich zum Aktivieren)
							</span>
						</label>
						{isLoadingAccounts ? (
							<div className="flex items-center gap-2 text-sm text-bgBase-800">
								<div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full" />
								Lade E-Mail-Konten...
							</div>
						) : connectedAccounts.length === 0 ? (
							<Alert
								tone="caution"
								title="E-Mail-Konto erforderlich"
								actionLabel="Zu den Einstellungen"
								onAction={() => onNavigate('/settings?tab=smtp')}
							>
								Keine verbundenen E-Mail-Konten verfügbar.
							</Alert>
						) : (
							<Select value={emailAccountId} onChange={(e) => setEmailAccountId(e.target.value)}>
								<option value="">-- Bitte wählen --</option>
								{connectedAccounts.map((acc) => (
									<option key={acc.id} value={acc.id}>
										{acc.emailAddress} ({acc.provider})
										{acc.authStrategy === 'oauth2' ? ' - OAuth2' : ' - SMTP'}
									</option>
								))}
							</Select>
						)}
					</div>
				</div>
			</Card>

			{/* Email Sequence Card - Only for Template Campaigns */}
			{campaign.campaignType === 'template' && (
				<Card>
					<div className="flex flex-row items-center justify-between space-y-0 pb-2">
						<div className="flex items-center gap-2">
							<BsEnvelope className="h-4 w-4 text-bgBase-800" />
							<h3 className="text-md font-semibold tracking-tight">E-Mail-Sequenz</h3>
						</div>
					</div>
					<div className="pt-0">
						<EmailSequenceTimeline
							emails={transformedEmails}
							campaignId={campaignId}
							emailAccountId={emailAccountId}
							listIds={selectedListIds}
							onDelete={onDeleteEmail}
						/>
					</div>
				</Card>
			)}
		</div>

		{/* Right Column - Recipients */}
		<div className="space-y-6">
			<RecipientsCard
				selectedLists={selectedLists}
				allContactLists={allContactLists}
				totalRecipients={totalRecipients}
				isEditingLists={isEditingLists}
				setIsEditingLists={setIsEditingLists}
				onListToggle={onListToggle}
				onNavigate={onNavigate}
			/>
			{campaign.campaignType === 'template' && <CustomVariablesCard campaign={campaign} />}
		</div>
	</div>
);

OverviewTab.propTypes = {
	campaign: PropTypes.object.isRequired,
	campaignId: PropTypes.string.isRequired,
	campaignName: PropTypes.string.isRequired,
	setCampaignName: PropTypes.func.isRequired,
	emailAccountId: PropTypes.string.isRequired,
	setEmailAccountId: PropTypes.func.isRequired,
	isLoadingAccounts: PropTypes.bool.isRequired,
	connectedAccounts: PropTypes.array.isRequired,
	transformedEmails: PropTypes.array.isRequired,
	onDeleteEmail: PropTypes.func.isRequired,
	selectedLists: PropTypes.array.isRequired,
	selectedListIds: PropTypes.arrayOf(PropTypes.string).isRequired,
	allContactLists: PropTypes.array.isRequired,
	totalRecipients: PropTypes.number.isRequired,
	isEditingLists: PropTypes.bool.isRequired,
	setIsEditingLists: PropTypes.func.isRequired,
	onListToggle: PropTypes.func.isRequired,
	onNavigate: PropTypes.func.isRequired,
};

export default OverviewTab;
